import React, {useEffect, useState} from 'react';
import {useForm} from "react-hook-form";
import {joiResolver} from "@hookform/resolvers/joi";
import {useDispatch, useSelector} from "react-redux";
import InputMask from "react-input-mask";
import toast, {Toaster} from 'react-hot-toast';

import copyIcon from "../../constants/images/copy-icon.svg";
import styles from './SettingsPageCustomer.module.scss';
import avatar from '../../constants/images/avatar.svg';
import upload from '../../constants/images/upload-icon.svg';
import {editProfileCustomerValidator} from "../../validators/editProfileCustomer.validator";
import {formatPhoneNumberToMask} from "../../constants/formatPhoneNumberToMask";
import {userService} from "../../services/user.service";
import {compressImage} from "../../utils/imageCompression";
import {formatPhoneNumber} from "../../constants/formatPhoneNumber";
import {resolveMediaUrl} from "../../configs/urls";
import {userDetailsActions} from "../../store/slices/userDetailsSlice";
import ModalPreviewSelectedPhoto from "../../components/ModalPreviewSelectedPhoto/ModalPreviewSelectedPhoto";
import {modalPreviewSelectedPhotoActions} from "../../store/slices/modalPreviewSelectedPhotoSlice";
import IsCustomer from "../../HOC/IsCustomer";

const SettingsPageCustomer = () => {
    const [error, setError] = useState(null);
    const [file, setFile] = useState(null);
    const [photo, setPhoto] = useState(null);
    const [isCopiedLink, setIsCopiedLink] = useState("");

    const dispatch = useDispatch();
    const {user} = useSelector(state => state.userDetails);
    const {isPreviewOpen} = useSelector(state => state.modalPreviewSelectedPhoto);

    const handleCreateFormData = (photo, user_data) => {
        let formData = new FormData();

        if (photo) {
            formData.append('photos_data', photo);
        }

        formData.append('user_data', JSON.stringify(user_data));

        return formData;
    };

    useEffect(() => {
        setValue('first_name', user.first_name);
        setValue('last_name', user.last_name);
        setValue('email', user.email);
        setValue('mobile_nummer', formatPhoneNumberToMask(user.mobile_nummer));
        setValue('address', user.address);
        setValue('house_number', user.house_number);
        setValue('apartment_number', user.apartment_number);
    }, [user])

    const {
        register,
        handleSubmit,
        formState: {errors, isValid},
        setValue,
    } = useForm({
        resolver: joiResolver(editProfileCustomerValidator),
        mode: 'all',
    });

    const onSubmit = async (data) => {
        try {
            // updating those data which !== '' and !== null
            const filledData = Object.fromEntries(
                Object.entries(data).filter(([key, value]) => value !== '' && value !== null)
            );

            const user_data = {
                ...filledData,
                mobile_nummer: formatPhoneNumber(data?.mobile_nummer),
            };
            const formData = handleCreateFormData(file, user_data);

            toast.promise(
                userService.updateById(user.id, formData),
                {
                    loading: "Оновлення даних...",
                    success: ({data}) => {
                        dispatch(userDetailsActions.setUser({...data}));
                        setPhoto(null);
                        setFile(null);
                        return "Ваші дані були успішно змінені";
                    },
                    error: "На жаль, не вдалось змінити дані. Спробуйте ще раз!",
                }
                , {id: "update-customer"})
                .catch((error) => {
                    console.error("Error updating data:", error);
                    setError(error.message);
                });

            setPhoto(null);
            setFile(null);
        } catch (error) {
            console.log(error)
            setError(error.message);
        }
    }

    useEffect(() => {
        return () => {
            toast.remove("update-customer");
        }
    }, []);

    const handleOpenFileExplorer = () => {
        const fileInput = document.getElementById('fileInput');

        if (fileInput) {
            fileInput.click();
        }
    }

    const handleUploadPhoto = async (e) => {
        const file = e.target.files[0];

        if (file) {
            const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg'];
            if (!allowedTypes.includes(file.type)) {
                toast.error('Дозволені тільки файли типу PNG, JPG, JPEG');
                return;
            }

            const maxSize = 20 * 1024 * 1024; // 20 МБ в байтах
            if (file.size > maxSize) {
                toast.error('Максимальний розмір файлу - 20 МБ');
                return;
            }

            // Shrink in the browser before upload; falls back to the original.
            const compressed = await compressImage(file);
            const photoUrl = URL.createObjectURL(compressed);
            setPhoto(photoUrl); // if user wants to update data, preview for profile photo
            setFile(compressed); // for db
        }
    };


    const handleSelectPhotoForPreview = () => {
        if (photo || user?.photos[0]) {
            dispatch(modalPreviewSelectedPhotoActions.openPreview('SettingsPageCustomer'));
        }
    };

    const handleCopyToClipboard = (url, role) => {
        navigator.clipboard.writeText(url)
          .then(() => {
              setIsCopiedLink(role);
          })
          .catch(err => {
              console.error("Error copying text:", err);
          });
    }

    return (
        <div className={styles.settingsPage}>
            <div className={styles.settingsPage_wrapper}>
                <h2 className={styles.settingsPage_wrapper_title}>Параметри</h2>

                <div className={styles.settingsPage_wrapper_settings}>
                    <div className={styles.settingsPage_wrapper_settings_uploadPhoto}>
                        <div className={styles.settingsPage_wrapper_settings_uploadPhoto_avatar}>
                            <img
                                onClick={handleSelectPhotoForPreview}
                                src={photo || resolveMediaUrl(user.photos[0]) || avatar}
                                alt="user`s photo for profile"
                            />
                        </div>

                        <input
                            id={'fileInput'}
                            type="file"
                            style={{display: 'none'}}
                            onChange={handleUploadPhoto}
                        />
                        <div
                            onClick={handleOpenFileExplorer}
                            className={styles.settingsPage_wrapper_settings_uploadPhoto_upload}
                        >
                            <img src={upload} alt="upload photo icon"/>
                            <p>Натисніть щоб завантажити нове фото</p>
                        </div>
                    </div>

                    <div className={styles.settingsPage_wrapper_settings_formContainer}>
                        <h3>Інформація про Вас</h3>

                        <form
                            onSubmit={handleSubmit(onSubmit)}
                            className={styles.settingsPage_wrapper_settings_formContainer_form}
                            action="#"
                        >
                            <div className={styles.settingsPage_wrapper_settings_formContainer_form_fullName}>
                                <div className={styles.labelContainer}>
                                    <label className={styles.label}>
                                        Прізвище
                                        <input
                                            {...register('last_name')}
                                            // placeholder={'Бойко'}
                                            className={styles.input}
                                            type="text"
                                        />
                                    </label>
                                    {errors.last_name &&
                                        <span className={styles.error}> {errors.last_name.message}</span>}
                                </div>

                                <div className={styles.labelContainer}>
                                    <label className={styles.label}>
                                        Ім’я
                                        <input
                                            {...register('first_name')}
                                            // placeholder={'Василь'}
                                            className={styles.input}
                                            type="text"
                                        />
                                    </label>
                                    {errors.first_name &&
                                        <span className={styles.error}> {errors.first_name.message}</span>}
                                </div>
                            </div>

                            <div className={styles.labelContainer}>
                                <label className={styles.label}>
                                    Email
                                    <input
                                        {...register('email')}
                                        className={`${styles.input} ${styles.email}`}
                                        // placeholder={'vasyl@gmail.com'}
                                        type="email"
                                    />
                                </label>
                                {errors.email && <span className={styles.error}> {errors.email.message}</span>}
                            </div>

                            <div className={styles.labelContainer}>
                                <label className={styles.label}>
                                    Номер телефону
                                    <InputMask
                                        className={`${styles.input} ${styles.phone}`}
                                        {...register('mobile_nummer')}
                                        mask="+38 (099) 999-99-99"
                                        // placeholder={'+38 (096) 456 34 56'}
                                        maskChar={null}
                                    />
                                </label>
                                {errors.mobile_nummer &&
                                    <span className={styles.error}> {errors.mobile_nummer.message}</span>}
                            </div>

                            <div className={styles.settingsPage_wrapper_settings_formContainer_form_addressContainer}>
                                <div className={`${styles.labelContainer} ${styles.address}`}>
                                    <label className={styles.label}>
                                        Адреса
                                        <input
                                            {...register('address')}
                                            className={styles.input}
                                            // placeholder={'С. Бандери'}
                                            type="text"
                                        />
                                    </label>
                                    {errors.address && <span className={styles.error}> {errors.address.message}</span>}
                                </div>

                                <div
                                    className={styles.settingsPage_wrapper_settings_formContainer_form_addressContainer_address}>
                                    <div className={`${styles.labelContainer} ${styles.details}`}>
                                        <label className={styles.label}>
                                            Номер будинку
                                            <input
                                                {...register('house_number')}
                                                className={styles.input}
                                                // placeholder={'32'}
                                                type="text"
                                            />
                                        </label>
                                        {errors.houseNumber &&
                                            <span className={styles.error}> {errors.houseNumber.message}</span>}
                                    </div>

                                    <div className={`${styles.labelContainer} ${styles.details}`}>
                                        <label className={styles.label}>
                                            Номер квартири
                                            <input
                                                {...register('apartment_number')}
                                                className={styles.input}
                                                // placeholder={'2'}
                                                type="text"
                                            />
                                        </label>
                                        {errors.apartmentNumber &&
                                            <span className={styles.error}> {errors.apartmentNumber.message}</span>}
                                    </div>
                                </div>
                            </div>

                            <div className={styles.settingsPage_wrapper_settings_formContainer_form_passwords}>
                                <div className={styles.labelContainer}>
                                    <label className={styles.label}>
                                        Пароль
                                        <input
                                            {...register('password')}
                                            className={styles.input}
                                            type="password"
                                        />
                                    </label>
                                    {error && <span className={styles.error}> {error}</span>}
                                    {errors.password &&
                                        <span className={styles.error}> {errors.password.message}</span>}
                                </div>

                                <div className={styles.labelContainer}>
                                    <label className={styles.label}>
                                        Повторити пароль
                                        <input
                                            {...register('confirm_password')}
                                            className={styles.input}
                                            type="password"
                                        />
                                    </label>
                                    {errors.confirm_password &&
                                        <span className={styles.error}> {errors.confirm_password.message}</span>}
                                </div>

                            </div>
                            <div className={`${styles.labelContainer} ${styles.ref}`}>
                                <span>Реферальне посилання</span>

                                <label className={styles.label}>
                                    Копіювати реферальне посилання
                                    {/*<input*/}
                                    {/*  className={styles.input}*/}
                                    {/*  disabled={true}*/}
                                    {/*  type="text"*/}
                                    {/*  value={`${process.env.REACT_APP_URL}/registration/customer?ref_id=${user.id}`}*/}
                                    {/*/>*/}

                                    <div className={styles.buttons}>
                                        <button
                                          onClick={() => handleCopyToClipboard(`${process.env.REACT_APP_URL}registration/cleaner?ref_id=${user.id}`, "cleaner")}
                                          type={"button"} className={`${styles.copy} ${styles.cleaner}`}>
                                            <img src={copyIcon} alt="copy icon"/>
                                            {isCopiedLink === "cleaner" ? "Скопійовано!" : "Для клінера"}
                                        </button>

                                        <button
                                          onClick={() => handleCopyToClipboard(`${process.env.REACT_APP_URL}registration/customer?ref_id=${user.id}`, "customer")}
                                          type={"button"} className={`${styles.copy} ${styles.customer}`}>
                                            <img src={copyIcon} alt="copy icon"/>
                                            {isCopiedLink === "customer" ? "Скопійовано!" : "Для замовника"}
                                        </button>
                                    </div>
                                </label>
                            </div>

                            <div className={styles.settingsPage_wrapper_settings_formContainer_form_button}>
                                <button
                                    type={"submit"}
                                    // disabled={!isValid}
                                    disabled={Object.keys(errors).length > 0}
                                    className={styles.button}
                                >Зберегти
                                </button>
                            </div>
                        </form>
                    </div>
                </div>

                {(isPreviewOpen && (photo || user?.photos[0])) &&
                    <ModalPreviewSelectedPhoto
                        photo={photo || resolveMediaUrl(user?.photos[0])}
                        component={'SettingsPageCustomer'}
                    />}

                {/* to appear hot toast notification */}
                <Toaster/>
            </div>
        </div>
    );
};

export default IsCustomer(SettingsPageCustomer);
